import type { ImportMethod } from '../db/schema';

/**
 * What a page gave up. Every field is optional because every field is missing
 * on some real retailer, and an item with nothing but a URL is still an item.
 *
 * There is deliberately no `brand`: `Item` has no column for one, and the
 * panel already shows the retailer's domain beside the price. Synthesising
 * "West Elm Andes Sofa" out of two fields is guesswork the store didn't ask
 * for.
 */
export type Extracted = {
  title: string | undefined;
  description: string;
  price: number | undefined;
  currency: string | undefined;
  /** Candidate image URLs, best first. Absolute, deduped, capped. */
  images: string[];
  /** Which parser won. Stored on the import record. */
  method: ImportMethod;
};

/**
 * Markup is written by templates, so whitespace is whatever the template did.
 * Collapses runs to a single space — including the non-breaking spaces
 * retailers pad prices with, which JavaScript's `\s` covers.
 */
export function clean(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;

  const collapsed = value.replace(/\s+/g, ' ').trim();
  return collapsed === '' ? undefined : collapsed;
}

/** The first thing in a value that reads as a non-empty string. */
export function firstString(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    for (const each of value) {
      const found = firstString(each);
      if (found !== undefined) return found;
    }
    return undefined;
  }

  if (isRecord(value)) {
    // schema.org lets a field that wants a string hold a whole object.
    return firstString(value['name'] ?? value['url'] ?? value['@value']);
  }

  return clean(value);
}

/**
 * "$1,299.00" → 1299. Prices arrive as numbers, as clean decimal strings, and
 * as whatever the page happened to render.
 *
 * The separator ambiguity is real: "1.299,00" is twelve hundred euros in most
 * of Europe and one-point-two-nine-nine anywhere else. Whichever separator
 * comes *last* is the decimal one, which resolves both without a locale.
 */
export function parsePrice(value: unknown): number | undefined {
  if (typeof value === 'number') return sane(value);

  const raw = clean(value);
  if (raw === undefined) return undefined;

  const digits = raw.replace(/[^\d.,]/g, '');
  if (digits === '') return undefined;

  const lastComma = digits.lastIndexOf(',');
  const lastDot = digits.lastIndexOf('.');

  let normalised: string;
  if (lastComma === -1 && lastDot === -1) {
    normalised = digits;
  } else if (lastComma > lastDot) {
    // Comma is the decimal point: strip dots, swap the comma.
    normalised = `${digits.slice(0, lastComma).replace(/[.,]/g, '')}.${digits.slice(lastComma + 1)}`;
  } else {
    normalised = `${digits.slice(0, lastDot).replace(/[.,]/g, '')}.${digits.slice(lastDot + 1)}`;
  }

  return sane(Number(normalised));
}

/** Zero is a real answer for a freebie. Negative money is a parsing accident. */
function sane(price: number): number | undefined {
  return Number.isFinite(price) && price >= 0 ? price : undefined;
}

/** ISO 4217 is three letters. Anything else is the page being creative. */
export function parseCurrency(value: unknown): string | undefined {
  const raw = clean(value);
  if (raw === undefined) return undefined;

  const code = raw.toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : undefined;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
